import { LitElement, css, html, nothing, unsafeHTML } from '@lit'
import styles from '@nova/styles'
import { formatPrice, updateCartQuantity, t } from '@nova/utils'

class NovaCartItem extends LitElement {
  static properties = {
    item: {
      type: Object,
    },
    compareAtPrice: {
      type: String,
    },
  }

  constructor() {
    super()
    this.item = {}
  }

  get formattedFinalPrice() {
    if (!this.item.final_line_price) return nothing
    return formatPrice(this.item.final_line_price)
  }

  get formattedCompareAtPrice() {
    if (Number(this.compareAtPrice) === 0) return nothing
    return formatPrice(this.compareAtPrice)
  }

  _renderOptions() {
    return this.item.options_with_values.map(
      (option) => html` <div class="cart-item__options flex">
        <dt>${option.name}:</dt>
        <dd>${option.value}</dd>
      </div>`,
    )
  }

  _handleQuantityChange = (event) => {
    this.updateQuantity(event.detail.value)
  }

  get isSampleProduct() {
    return this.item.properties['_is_sample'] ||
      (Array.isArray(this.item.properties) && this.item.properties.find(prop => prop[0] === '_is_sample'))
  }

  _renderProperties = () => {
    if (Array.isArray(this.item.properties)) {
      return this.item.properties.filter(property => property[0][0] !== '_')
        .map(
          (property) => html` <div class="cart-item__options flex">
            <span>${property[0]}:</span>
            <span>${property[1]}</span>
          </div>`,
        )
    } else if (Object.keys(this.item.properties).length) {
      return Object.keys(this.item.properties).filter(key => key[0] !== '_')
        .map(
          (key) => html` <div class="cart-item__options flex">
            <span>${key}:</span>
            <span>${this.item.properties[key]}</span>
          </div>`,
        )
    }
    return nothing
  }

  async updateQuantity(quantity) {
    this.loading = true
    await updateCartQuantity(
      [
        {
          id: this.item.key,
          quantity,
        },
      ],
      false,
    )

    await this.reloadMiniCart()
    this.dispatchEvent(new CustomEvent('nova-change'))
    this.loading = false
  }

  async reloadMiniCart() {
    try {
      const productHandle = this.item.handle
      const response = await fetch(`/products/${productHandle}?view=cart`)
      const text = await response.text()
      const html = new DOMParser().parseFromString(text, 'text/html')
      const content = html.querySelector('.view-mini-cart').innerHTML
      const $miniCartContainer = document.querySelector('.js-mini-cart-container')
      if ($miniCartContainer) {
        $miniCartContainer.innerHTML = content
      }
    } catch (error) {
      console.error('Error:', error.message)
    }
  }

  render() {
    return html`
      <div class="cart-item__media">
        <a href=${this.item.url}>
          ${this.item.image_html ? html`${unsafeHTML(decodeURIComponent(this.item.image_html).replace(/\+/g, ' '))}` : nothing}
        </a>
      </div>
      <div class="cart-item__content js-qty-container">
        <div class="cart-item__info">
          <a class="cart-item__title" href=${this.item.url}>${this.item.product_title}</a>
          <div class="cart-item__price-wrapper">
            ${
              this.item.original_line_price > this.item.final_line_price
                ? html`
                  <dl class="cart-item__discounted-prices">
                    <dt class="visually-hidden">Sale Price</dt>
                    <dd>${this.formattedFinalPrice}</dd>
                    <dt class="visually-hidden">Regular Price</dt>
                    <dd>
                      <s class="cart-item__compare-at-price">
                        ${this.formattedCompareAtPrice}
                      </s>
                    </dd>
                  </dl>
                `
                : html`<span>${this.formattedFinalPrice} </span>`
            }
          </div>
          ${this.item.options_with_values ? this._renderOptions() : nothing}
          ${this._renderProperties()}
        </div>
        <div class="cart-item__actions">
          <div class="hidden js-qty-error-message qty-error-message">${this.item.min}${t('products.product.size_requirements.tabletop.message_without_min')}</div>
          <div class="caption c-danger hidden pt-xs js-qty-error-message-limit qty-error-message">
            ${this.item.limitStr}<a href="mailto:${this.item.contactEmail}">${this.item.contactEmail}</a>
          </div>
          <div class="cart-item__actions-wrapper">
            ${
              this.isSampleProduct
                ? html`<span class="cart-item__quantity-input cart-item__quantity-view">${this.item.quantity}</span>`
                : html`
                  <div class="flex flex-items-center col-gap-2xs">
                    <nova-quantity-input
                      initial=${this.item.quantity}
                      @changeQuantityStarted=${this._handleQuantityChange}
                      class="cart-item__quantity-input ${this.item.unit && 'cart-item__quantity-input-unit'}"
                      min=${this.item.min}
                      max=${this.item.max}
                      limit=${this.item.limit}
                      show-buttons="${this.item.showButtons}"
                    ></nova-quantity-input>
                    ${this.item.unit ? html `<span class="caption c-grey-400">${this.item.unit}</span>` : nothing}
                  </div>
                `
            }
            <sl-button
              variant="text"
              class="cart-item__remove"
              @click=${() => this.updateQuantity(0)}
            >
              Remove
            </sl-button>
          </div>
        </div>
      </div>
    `
  }

  static styles = [
    styles.flex,
    css`
      :host {
        width: 100%;
        padding-block: var(--nova-space-sm);
        display: flex;
        gap: var(--nova-space-sm);
        --media-width: 180px;

        @media screen and (min-width: 1024px) {
          padding-block: var(--nova-space-xsm);
          gap: var(--nova-space-xsm);
        }
      }
      .cart-item__media {
        width: var(--media-width);
        height: var(--media-width);
        min-width: var(--media-width);
        display: flex;
      }
      .cart-item__media a {
        display: flex;
        width: 100%;
        height: 100%;
      }
      .cart-item__media img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
      .cart-item__content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: var(--nova-space-sm);

        @media screen and (min-width: 1024px) {
          gap: var(--nova-space-xsm);
        }
      }
      .cart-item__info {
        display: flex;
        flex-direction: column;
        gap: var(--nova-space-2xs);
      }
      .cart-item__actions {
        display: flex;
        flex-direction: column;
        gap: var(--nova-space-2xs);
      }
      .cart-item__actions-wrapper {
        display: flex;
        gap: var(--nova-space-md);
        align-items: center;
        justify-content: space-between;

        @media screen and (min-width: 1024px) {
          gap: var(--nova-space-sm);
        }
      }
      .cart-item__title,
      .cart-item__price-wrapper {
        color: var(--nova-color-grey-600);
        text-decoration: none !important;
        text-transform: capitalize;
        font: var(--nova-body-font);
        letter-spacing: var(--nova-body-letter-spacing);
        display: flex !important;
        gap: var(--nova-space-2xs);
      }
      .cart-item__options,
      .cart-item__compare-at-price {
        text-transform: capitalize;
        font: var(--nova-body-caption);
	      letter-spacing: var(--nova-caption-letter-spacing);
        color: var(--nova-color-grey-400);
      }
      .cart-item__options {
        display: flex;
        gap: var(--nova-space-2xs);

        & dt {
          display: none;
        }

        & dd {
          margin-inline-start: 0;
        }
      }
      .cart-item__price-wrapper {
        & dl {
          margin-block: 0;
          display: flex !important;
          gap: var(--nova-space-2xs);
          align-items: center;
        }
        & dd {
          margin-inline-start: 0;
        }
      }
      .cart-item__price-wrapper .visually-hidden {
        visibility: hidden;
        display: none;
      }

      .cart-item__remove::part(base) {
        color: var(--nova-color-grey-400);
        text-decoration: underline;
        text-underline-offset: var(--nova-space-2xs);
        font: var(--nova-body-utility);
	      letter-spacing: var(--nova-utility-letter-spacing);
	      text-transform: var(--nova-utility-text-transform);
        width: min-content;
        min-height: 21px;
      }
      .cart-item__remove::part(label) {
        padding: 0;
      }
      .cart-item__quantity-input {
        display: flex;
        justify-content: end;
        width: 82px;
      }
      .cart-item__quantity-input-unit {
        width: 54px;
      }
      .cart-item__quantity-view {
        align-items: center;
        justify-content: flex-start;
        color: var(--nova-color-grey-600);
      }
      .qty-error-message {
        font: var(--nova-body-caption);
        letter-spacing: var(--nova-caption-letter-spacing);
        text-transform: var(--nova-caption-text-transform);
        color: var(--nova-color-danger);

        & a {
          color: inherit;
        }
      }
      .hidden {
        display: none;
      }
      .flex-items-center {
        align-items: center;
      }
      .col-gap-2xs {
        column-gap: var(--nova-space-2xs);
      }
      .c-grey-400 {
        color: var(--nova-color-grey-400);
      }
    `,
  ]
}

customElements.define('nova-cart-item', NovaCartItem)
